import React, { useState, useMemo, type Dispatch, type SetStateAction } from "react";
import moment from "moment-timezone";
import { v4 as uuidV4 } from "uuid";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MultiSelect } from "@/components/ui/selectMulti";
import { useToast } from "@/components/ui/use-toast";
import { type CfrSuggestion, type ListCfrTemplateItem } from "@/lib/api/cfr";
import { CommodityOptions } from "@/lib/api/commodity";
import { getUniqueStrings } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { useBulkSendBatchSubmitter, type BulkSendInstancePayload } from "./useBulkSendBatchSubmitter";
import { BulkSendBatchProgressView } from "./BulkSendBatchProgressView";

interface BulkSendDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    selectedSuggestions: CfrSuggestion[];
    templateData: ListCfrTemplateItem[];
}

export function BulkSendDialog({
    open,
    onOpenChange,
    selectedSuggestions,
    templateData,
}: BulkSendDialogProps) {
    const { toast } = useToast();
    const { phase, isSubmitting, submit, close } = useBulkSendBatchSubmitter({
        onBeforeRedirect: () => {
            setSelectedCommodities([]);
            onOpenChange(false);
        },
    });

    // Get unique crop years from templates
    const cropYearOptions = useMemo(() => {
        const uniqueYears = Array.from(new Set(templateData.map(t => t.crop_year)))
            .sort((a, b) => b - a);
        return uniqueYears.map(year => ({
            value: year.toString(),
            label: year.toString(),
        }));
    }, [templateData]);

    // Get unique commodities from templates
    const commodityOptions = useMemo(() => {
        const uniqueCommodities = getUniqueStrings(templateData.map(t => t.commodity_type));
        return uniqueCommodities
            .map(commodity => {
                const option = CommodityOptions.find(opt => opt.value === commodity);
                return {
                    value: commodity,
                    label: option?.label || commodity,
                };
            })
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [templateData]);

    const currentYear = moment().year();
    const [selectedCropYear, setSelectedCropYear] = useState<string>(
        cropYearOptions.find(opt => opt.value === currentYear.toString())?.value ||
        cropYearOptions[0]?.value ||
        currentYear.toString()
    );
    const [selectedCommodities, setSelectedCommodities] = useState<string[]>([]);
    const [isTestSend, setIsTestSend] = useState(false);

    const matchingTemplates = useMemo(() => {
        if (!selectedCropYear || !selectedCommodities?.length) return [];

        const selectedYear = parseInt(selectedCropYear);

        return templateData.filter(template =>
            template.crop_year === selectedYear &&
            selectedCommodities.includes(template.commodity_type)
        );
    }, [templateData, selectedCropYear, selectedCommodities]);

    const handleClose = () => {
        if (isSubmitting) { return; }
        close();
        onOpenChange(false);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (isSubmitting) return;

        // ----- Pre-flight validation (synchronous) -----
        if (selectedCommodities.length === 0) {
            toast({ title: "Error", description: "Please select at least one commodity", variant: "destructive" });
            return;
        }
        if (selectedSuggestions.length === 0) {
            toast({ title: "Error", description: "No suggestions selected", variant: "destructive" });
            return;
        }
        if (matchingTemplates.length === 0) {
            toast({ title: "Error", description: "No templates found matching the selected criteria", variant: "destructive" });
            return;
        }

        const validSuggestions = selectedSuggestions.filter((s) => {
            if (!s.customer_id) {
                console.warn("Skipping suggestion without customer_id", s);
                return false;
            }
            return true;
        });
        if (validSuggestions.length === 0) {
            toast({ title: "Error", description: "No valid suggestions to send (missing customer information)", variant: "destructive" });
            return;
        }

        // ----- Build all staged payloads -----
        const payloads: BulkSendInstancePayload[] = [];
        for (const suggestion of validSuggestions) {
            for (const template of matchingTemplates) {
                payloads.push({
                    cfr_document_id: template.id,
                    group_id: uuidV4(),
                    template_id: template.DocumentTemplate!.id,
                    customer_id: suggestion.customer_id!,
                    is_test_send: isTestSend,
                    is_draft: false,
                    suggestion_id: suggestion.id,
                });
            }
        }

        await submit(payloads);
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) { handleClose(); } else { onOpenChange(o); } }}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>Bulk Send CFR Templates</DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit}>
                    {phase.kind !== "idle" ? (
                        <BulkSendBatchProgressView phase={phase} onClose={handleClose} />
                    ) : (
                        <BulkSendForm
                            selectedSuggestionsCount={selectedSuggestions.length}
                            matchingTemplatesCount={matchingTemplates.length}
                            isTestSend={isTestSend}
                            setIsTestSend={setIsTestSend}
                            cropYearOptions={cropYearOptions}
                            commodityOptions={commodityOptions}
                            selectedCropYear={selectedCropYear}
                            setSelectedCropYear={setSelectedCropYear}
                            selectedCommodities={selectedCommodities}
                            setSelectedCommodities={setSelectedCommodities}
                            onCancel={() => onOpenChange(false)}
                        />
                    )}
                </form>
            </DialogContent>
        </Dialog>
    );
}

function BulkSendForm({
    selectedSuggestionsCount,
    matchingTemplatesCount,
    isTestSend,
    setIsTestSend,
    cropYearOptions,
    commodityOptions,
    selectedCropYear,
    setSelectedCropYear,
    selectedCommodities,
    setSelectedCommodities,
    onCancel,
}: {
    selectedSuggestionsCount: number;
    matchingTemplatesCount: number;
    isTestSend: boolean;
    setIsTestSend: (v: boolean) => void;
    cropYearOptions: { value: string; label: string }[];
    commodityOptions: { value: string; label: string }[];
    selectedCropYear: string;
    setSelectedCropYear: (v: string) => void;
    selectedCommodities: string[];
    setSelectedCommodities: Dispatch<SetStateAction<string[]>>;
    onCancel: () => void;
}) {
    return (
        <>
            <div className="grid gap-4 py-4">
                <div className="grid gap-2 md:grid-cols-2">
                    <div className="grid gap-2">
                        <Label>Selected Customers</Label>
                        <div className="text-sm text-muted-foreground">
                            {selectedSuggestionsCount} customer(s) selected
                        </div>
                    </div>
                    <div className="grid gap-2">
                        <Label>Implied Templates</Label>
                        <div className="text-sm text-muted-foreground">
                            {matchingTemplatesCount} templates(s) selected
                        </div>
                    </div>
                </div>

                <div className="grid gap-2">
                    <Label htmlFor="is_test_send">Is Test Send</Label>
                    <Checkbox
                        id="is_test_send"
                        checked={isTestSend}
                        onCheckedChange={(checked) => setIsTestSend(!!checked)}
                    />
                </div>

                <div className="grid gap-2">
                    <Label htmlFor="crop_year">Crop Year</Label>
                    <Select value={selectedCropYear} onValueChange={setSelectedCropYear}>
                        <SelectTrigger>
                            <SelectValue placeholder="Select crop year" />
                        </SelectTrigger>
                        <SelectContent>
                            {cropYearOptions.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="grid gap-2">
                    <Label htmlFor="commodities">Commodities</Label>
                    <MultiSelect
                        values={selectedCommodities}
                        setValues={setSelectedCommodities}
                        options={commodityOptions}
                        listClassName="max-h-[200px]"
                    />
                    <div className="text-xs text-muted-foreground">Select one or more commodities</div>
                </div>
            </div>

            <DialogFooter>
                <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
                <Button type="submit">Send</Button>
            </DialogFooter>
        </>
    );
}
